/**
 * Rating feature: pill with 5 stars, submit, visibility on "rating slide".
 */

import { createEl } from '../core/dom';
import { starIcon, fireConfetti, isRatingSlideVisible } from '../lib/helpers';
import type { LecturaHostAdapter, RatingStorage } from '../core/types';

export interface RatingConfig {
  presentationId: string;
  ratingLabel?: string;
  showRatingAfterSlide?: 'last' | number;
  formEndpoint?: string;
  ratingFormEndpoint?: string;
  confettiOnRate?: boolean;
  chat?: { firebaseConfig?: unknown };
}

export interface RatingApi {
  renderRatingPill: () => void;
  refreshRatingPill: () => void;
}

export function registerRating(
  config: RatingConfig,
  adapter: LecturaHostAdapter,
  storage: RatingStorage
): RatingApi {
  let pill: HTMLElement | null = null;
  let starButtons: NodeListOf<HTMLElement> | null = null;
  let autoHideTimer: ReturnType<typeof setTimeout> | null = null;
  let currentRating = 0;

  const getSlideIndex = (): number => adapter.getSlideIndex();
  const getTotalSlides = (): number => adapter.getTotalSlides();
  const visible = (): boolean => isRatingSlideVisible(config, getSlideIndex, getTotalSlides);

  function highlightStars(upTo: number): void {
    if (!starButtons) return;
    starButtons.forEach((btn, i) => {
      const filled = i < upTo;
      btn.classList.toggle('rf-active', filled);
      const icon = starIcon(filled);
      if (btn.firstChild) btn.replaceChild(icon, btn.firstChild);
      else btn.appendChild(icon);
    });
  }

  function scheduleAutoHide(): void {
    if (autoHideTimer) clearTimeout(autoHideTimer);
    const p = pill;
    if (!p) return;
    autoHideTimer = setTimeout(() => {
      autoHideTimer = null;
      p.classList.add('rf-pill-fade-out');
      setTimeout(() => {
        p.classList.add('rf-hidden');
        p.classList.remove('rf-pill-fade-out');
      }, 400);
    }, 3500);
  }

  function submitRating(value: number): void {
    if (!pill) return;
    currentRating = value;
    highlightStars(value);

    const user = adapter.getCurrentUser();
    if (config.chat?.firebaseConfig && user && storage.recordRating) {
      storage.recordRating(config.presentationId, value);
    } else {
      storage.recordRating(config.presentationId, value);
    }

    const endpoint = config.ratingFormEndpoint || config.formEndpoint;
    if (endpoint) {
      const formData = new FormData();
      formData.append('rating', String(value));
      formData.append('presentation', config.presentationId);
      formData.append('_subject', 'Presentation rating: ' + config.presentationId);
      fetch(endpoint, { method: 'POST', body: formData, headers: { Accept: 'application/json' } }).catch(() => {});
    }

    if (config.confettiOnRate) {
      fireConfetti();
    }

    const labelEl = pill.querySelector('.rf-rating-label');
    if (labelEl) labelEl.textContent = 'Thank you!';
    scheduleAutoHide();
  }

  function renderRatingPill(): void {
    if (pill?.parentNode) return;
    pill = createEl('div', { className: 'rf-rating-pill rf-hidden' });

    const label = createEl('span', { className: 'rf-rating-label' });
    label.textContent = config.ratingLabel || 'Rate this presentation';
    pill.appendChild(label);

    const starsWrap = createEl('div', { className: 'rf-stars' });
    for (let i = 1; i <= 5; i++) {
      const btn = createEl('button', {
        type: 'button',
        className: 'rf-star',
        'aria-label': 'Rate ' + i + ' stars',
        'data-value': String(i)
      });
      btn.appendChild(starIcon(false));
      const val = i;
      btn.addEventListener('click', () => submitRating(val));
      btn.addEventListener('mouseenter', () => highlightStars(val));
      btn.addEventListener('mouseleave', () => highlightStars(currentRating));
      starsWrap.appendChild(btn);
    }
    pill.appendChild(starsWrap);

    const closeBtn = createEl('button', {
      type: 'button',
      className: 'rf-rating-close',
      'aria-label': 'Close'
    });
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => {
      if (pill) pill.classList.add('rf-hidden');
    });
    pill.appendChild(closeBtn);

    const revealPane = document.querySelector('.rf-slides-chat-wrap .reveal') as HTMLElement | null;
    const host: HTMLElement = revealPane || document.body;
    if (revealPane) pill.classList.add('rf-rating-pill-in-reveal');
    host.appendChild(pill);
    starButtons = pill.querySelectorAll('.rf-star');
  }

  function refreshRatingPill(): void {
    storage.getMyRating(config.presentationId).then((stars) => {
      if (stars != null && stars > 0) currentRating = stars;
      if (!pill) return;
      const show = visible();
      pill.classList.toggle('rf-hidden', !show);
      if (show) {
        const labelEl = pill.querySelector('.rf-rating-label');
        if (labelEl) labelEl.textContent = config.ratingLabel || 'Rate this presentation';
        highlightStars(currentRating);
        if (currentRating > 0) scheduleAutoHide();
        else if (autoHideTimer) {
          clearTimeout(autoHideTimer);
          autoHideTimer = null;
        }
      } else if (autoHideTimer) {
        clearTimeout(autoHideTimer);
        autoHideTimer = null;
      }
    });
  }

  return { renderRatingPill, refreshRatingPill };
}
